import { Entity } from 'engine/Entity';
import { EngineContext } from 'engine/components/EngineProvider';
import { V2, addV2, emptyV2 } from 'engine/lib/V2';
import { Message, message } from 'game/components/Message';
import { Modal, ModalBody } from 'game/components/Modal';
import { Tooltip } from 'game/components/Tooltip';
import { createStyles } from 'game/lib/createStyles';
import { AnyModule, BotState, InventoryOperation, MODULE_UNIT_SIZE, Module, ModuleSize, ModuleType } from 'game/state/BotState';
import { colors } from 'game/styles/theme';
import { useContext, useMemo, useRef } from 'react';
import { BotModule } from '../BotModule';

const append = (affix: string) => (str: string | number) => `${str}${affix}`;

const classes = createStyles({
  window: {
    overflow: 'auto',
    borderRadius: 8,
    height: '100%',
  },
  module: {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
  },

  list: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -30,
    marginTop: -10,
    '& > *': {
      marginLeft: 30,
      marginTop: 10,
    },
  },
  subList: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    marginLeft: -10,
    marginTop: -10,
    '& > *': {
      marginLeft: 10,
      marginTop: 10,
    },
  },
  label: {
    display: 'block',
    color: colors.darkBlue,
    marginBottom: 5,
  },
  tooltip: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 360,
  },
});

interface AddModuleProps {
  bot: Entity<{ state: BotState }>,
  modulesScale: number,
  onClose: () => void,
  onAddModule: (module: Module, screenPos: V2) => void,
}

const emptyModule: AnyModule = {
  type: ModuleType.Cpu,
  id: 0,
  position: emptyV2,
  inputs: {},
  config: null,
};

interface ModuleOption {
  name: React.ReactNode,
  body: React.ReactNode,
  module: Module,
}

interface ModuleGroup {
  label: React.ReactNode,
  options: ModuleOption[],
}

const getModuleGroups = (bot: Entity<{ state: BotState }>): ModuleGroup[] => [
  {
    label: <Message text="Sensors" />,
    options: [
      {
        name: <Message text="Inventory" />,
        body: (
          <>
            <p>
              <Message text="Send a signal about the bot's inventory." />
            </p>
            <p>
              <span><Message text="LACKS" /></span>
              {' - '}
              <Message text="If the bot lacks the specified item, this outputs a signal containing that item." />
            </p>
            <p>
              <span><Message text="HAS" /></span>
              {' - '}
              <Message text="If the bot has the specified item, this outputs a signal containing that item." />
            </p>
          </>
        ),
        module: {
          ...emptyModule,
          type: ModuleType.Inventory,
          config: {
            operation: InventoryOperation.Lacks,
            selection: null,
          },
        },
      },
      {
        name: <Message text="Nearby" />,
        body: <p><Message text="Find a specific item nearby. If multiple matches are found, the closest one is output. If no matches are found, no signal is output." /></p>,
        module: {
          ...emptyModule,
          type: ModuleType.Nearby,
          config: { manualSelection: null },
        },
      },
    ],
  },
  {
    label: <Message text="Actions" />,
    options: [
      {
        name: <Message text="Use" />,
        body: <p><Message text="Tell the bot to use a specific tool on a specific target." /></p>,
        module: {
          ...emptyModule,
          type: ModuleType.Use,
          config: { tool: null },
        },
      },
      {
        name: <Message text="Follow" />,
        body: <p><Message text="Tell the bot to follow a specific target" /></p>,
        module: {
          ...emptyModule,
          type: ModuleType.Follow,
          config: null,
        },
      },
      {
        name: <Message text="Move to" />,
        body: <p><Message text="Tell the bot to move to a specific location." /></p>,
        module: {
          ...emptyModule,
          type: ModuleType.MoveTo,
          config: { target: [...bot.transform.position] },
        },
      },
    ],
  },
  {
    label: <Message text="Logic" />,
    options: [
      {
        name: <Message text="Fallback" />,
        body: (
          <>
            <p><Message text={'Process the inputs from top to bottom until one "works" (like an OR gate).'} /></p>
            <p><Message text="When a success is found or all inputs are processed, this outputs the signal of the last input it processed. Otherwise, it outputs the signal of the input it's currently processing." /></p>
          </>
        ),
        module: {
          ...emptyModule,
          type: ModuleType.Logic,
          operation: 'fallback',
          config: null,
        },
      },
      {
        name: <Message text="Sequence" />,
        body: (
          <>
            <p><Message text={'Process the inputs from top to bottom until one "fails" (like an AND gate).'} /></p>
            <p><Message text="When a failure is found or all inputs are processed, this outputs the signal of the last input it processed. Otherwise, it outputs the signal of the input it's currently processing." /></p>
          </>
        ),
        module: {
          ...emptyModule,
          type: ModuleType.Logic,
          operation: 'sequence',
          config: null,
        },
      },
    ],
  },
];

interface ModuleComponentProps {
  bot: Entity<{ state: BotState }>,
  module: Module,
  scale: number,
  onGrabStart: React.MouseEventHandler,
}

const ModuleComponent: React.FC<ModuleComponentProps> = ({ bot, module, scale, onGrabStart }) => {
  const size = ModuleSize[module.type];
  const [w, h] = size.map(n => n * MODULE_UNIT_SIZE);
  return (
    <svg className={classes.module} viewBox={`0 0 ${w} ${h}`} width={w * scale} height={h * scale}>
      <BotModule
        bot={bot}
        module={module}
        grabbing={false}
        onGrabStart={onGrabStart}
      />
    </svg>
  );
};

export const AddModule: React.FC<AddModuleProps> = ({ bot, modulesScale, onClose, onAddModule }) => {
  const over = useRef<ModuleOption | null>(null);
  const { input } = useContext(EngineContext);
  const moduleGroups = useMemo(() => getModuleGroups(bot), [bot]);

  return (
    <>
      <Modal heading={message('Add module')} onClose={onClose}>
        <ModalBody>
          <ul className={classes.list}>
            {moduleGroups.map((g, i) => (
              <li key={i}>
                <ul className={classes.subList}>
                  {g.options.map((o, j) => (
                    <li key={j}>
                      {j === 0 && <span className={classes.label}>{g.label}</span>}
                      <div
                        onMouseEnter={() => { over.current = o; }}
                        onMouseLeave={() => { over.current = null; }}
                      >
                        <ModuleComponent
                          bot={bot}
                          module={o.module}
                          scale={modulesScale}
                          onGrabStart={e => {
                            if (e.button !== 0) return;
                            const { left, top } = e.currentTarget.getBoundingClientRect();
                            onAddModule(o.module, [left, top]);
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </ModalBody>
      </Modal>
      {over.current && (
        <div
          className={classes.tooltip}
          style={{
            transform: `translate(${addV2([10, 10], input.mouse).map(append('px'))})`,
          }}
        >
          <Tooltip heading={over.current.name}>{over.current.body}</Tooltip>
        </div>
      )}
    </>
  );
};
